import React from 'react';
import { Modal, Form, Input, Button, Alert } from 'antd';
import { useMutation } from '@tanstack/react-query';
import LocalStorageUtility from '@/utils/localStorage';
import { LocalStorageKeys } from 'src/enums/ELocalStorage';
import { affiliateLogin } from '@/api/user';

interface LoginModalProps {
	visible: boolean;
	onLoginSuccess?: (affiliateData: any) => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ visible, onLoginSuccess }) => {
	const [form] = Form.useForm();

	const loginMutation = useMutation({
		mutationKey: ['loginModalApi'],
		mutationFn: (email: string) => affiliateLogin(email),
		onSuccess: (data: any) => {
			LocalStorageUtility.setLocalData(LocalStorageKeys.AFFILIATE_DATA, data.affiliateData);

			if (onLoginSuccess) {
				onLoginSuccess(data.affiliateData);
			}
		},
	});

	const handleSubmit = (values: any) => {
		loginMutation.mutate(values.username);
	};

	return (
		<Modal
			title="Login Required"
			open={visible}
			footer={null}
			closable={false}
			maskClosable={false}
			keyboard={false}
			centered
			width={400}
			className="!rounded-xl !shadow-2xl"
			styles={{
				header: {
					borderBottom: '1px solid #f0f0f0 !important',
					padding: '20px 24px 16px !important',
					borderRadius: '12px 12px 0 0 !important',
				},
				body: {
					padding: '0 !important',
				},
			}}
		>
			<div className="p-4">
				<p className="text-gray-600 mb-6">Please enter your credentials to continue.</p>

				<Form form={form} layout="vertical" onFinish={handleSubmit} autoComplete="off">
					<Form.Item
						label={<span className="!font-medium !text-gray-700">Email</span>}
						name="username"
						rules={[
							{
								required: true,
								message: 'Email is required!',
							},
							{
								type: 'email',
								message: 'Please enter a valid email address!',
							},
						]}
					>
						<Input
							placeholder="Enter your email"
							size="large"
							disabled={loginMutation.isPending}
							className="!rounded-lg !border-gray-300 hover:!border-blue-500 focus:!border-blue-500 focus:!shadow-blue-100"
						/>
					</Form.Item>

					{loginMutation.isError && (
						<Form.Item>
							<Alert
								message={loginMutation.error?.message || 'Login failed'}
								type="error"
								showIcon
								className="!rounded-lg !border-l-4 !border-l-red-500 !bg-red-50"
							/>
						</Form.Item>
					)}

					<Form.Item className="mb-0">
						<Button
							type="primary"
							htmlType="submit"
							loading={loginMutation.isPending}
							disabled={loginMutation.isPending}
							className="!w-full !h-11 !rounded-lg !font-medium !bg-gradient-to-r !from-blue-600 !to-indigo-600 hover:!from-blue-700 hover:!to-indigo-700 !border-0 !shadow-lg hover:!shadow-xl hover:!-translate-y-0.5 !transition-all !duration-200"
							size="large"
						>
							{loginMutation.isPending ? 'Logging in...' : 'Login'}
						</Button>
					</Form.Item>
				</Form>
			</div>
		</Modal>
	);
};

export default LoginModal;
